import { useTranslation } from "@i18n";
import { useState, Suspense, lazy, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@Reducers/index";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import ReviewToolbar from "@Components/TakeAction/TakeActionToolBar";
import UserSupport from "@Features/jira/components/userSupport";
import IconComponent from "@CubeComponents/icons";
import { getAllNotificationPageUrl } from "@Services/utils/helpers";
import { shouldShowSupport } from "@Utils";

const AllNotifications = lazy(() => import("./NotificationsPages"));
const Escrow = lazy(() => import("@Components/Escrow"));
const TakeAction = lazy(() => import("@Components/TakeAction"));
const ExportedFiles = lazy(() => import("./ExportedFilesTab"));

function Notifications() {
  const { t } = useTranslation();
  // `null` when the URL carries no `section`; the effect below writes the raw query value through.
  const [activeTab, setActiveTab] = useState<string | null>("ALL_NOTIFICATIONS");
  const [searchParams] = useSearchParams();
  // Due-date bucket key read by `@Components/TakeAction` ("overdue" | "next7days" | "next30days"),
  // cleared to `null` when the active chip is clicked again.
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  // Branded: these are `projects._id` values, and every consumer of this state
  // (ReviewToolbar.onProjectIdChange, TakeAction.selectedProjectIds) already declares ObjectIdString[].
  const [selectedProjectIds, setSelectedProjectIds] = useState<ObjectIdString[]>([]);
  const handleFilterChange = (key: string | null) => setActiveFilter(key);
  const handleProjectIdsChange = (key: ObjectIdString[]) => setSelectedProjectIds(key);

  const section = searchParams.get("section");
  const navigate = useNavigate();
  const portalData = useSelector((state: RootState) => state.portalData);

  const activeButton = "text-base text-[color:var(--primary-text)] font-semibold";
  const singleButtonStyle = "text-base text-[color:var(--primary-text)] cursor-pointer";

  useEffect(() => {
    if (section === "support" && portalData.id && !shouldShowSupport(portalData)) {
      navigate(getAllNotificationPageUrl({ section: "allNotifications" }), { replace: true });
      return;
    }
    setActiveTab(section);
  }, [section, navigate, portalData]);

  const handleNavigate = (section: string) => {
    if (section == "takeAction") {
      navigate(getAllNotificationPageUrl({ section, module: "reviews" }), { replace: true });
    } else {
      navigate(getAllNotificationPageUrl({ section }), { replace: true });
    }
  };

  const allTabs = [
    {
      name: t("All Notifications"),
      id: "allNotifications",
    },
    {
      name: t("Take Action"),
      id: "takeAction",
    },
    {
      name: t("Escrow"),
      id: "escrow",
    },
    ...(shouldShowSupport(portalData)
      ? [
          {
            name: t("Support"),
            id: "support",
          },
        ]
      : []),
    {
      name: t("Exported Files"),
      id: "exportedFiles",
    },
  ];

  return (
    <div className="flex flex-col bg-surface_neutral h-full px-6">
      <div className="flex flex-col px-4 py-2 bg-white">
        <div className="flex items-center gap-2 font-semibold text-2xl">
          <IconComponent name="SettingsIcon" />
          {t("My Dashboard")}
        </div>
        <div className="text-base">{t("View notifications, take action and escrow files for the user")}</div>
      </div>
      <div className="w-full flex overflow-auto flex-col bg-white h-full">
        <div
          className="flex h-14 gap-8 ps-8 border-y border-surface_stroke_gradient_color1"
          style={{
            borderImageSource: "linear-gradient(92deg, #ecf3fe 30%, rgba(236, 243, 254, 0.15) 111%)",
            borderImageSlice: 1,
          }}
        >
          {allTabs.map((tab: (typeof allTabs)[number]) => (
            <div key={tab.id} className="flex flex-col items-center w-fit gap-1 pt-3.5">
              <div
                className={activeTab === tab.id ? activeButton : singleButtonStyle}
                onClick={() => handleNavigate(tab.id)}
              >
                {tab.name}
              </div>
              {activeTab === `${tab.id}` && (
                <div
                  className="min-w-max h-1 w-full rounded-md"
                  style={{
                    backgroundImage: "linear-gradient(95deg, #6bbbe3 0%, #3e8ab0 110%)",
                  }}
                />
              )}
            </div>
          ))}
        </div>
        {activeTab === "takeAction" && (
          <ReviewToolbar
            activeFilter={activeFilter}
            onFilterChange={handleFilterChange}
            selectedProjectIds={selectedProjectIds}
            onProjectIdChange={handleProjectIdsChange}
          />
        )}
        <div className="h-full w-full overflow-auto flex grow flex-1">
          {activeTab === "allNotifications" && (
            <Suspense fallback={<></>}>
              <AllNotifications />
            </Suspense>
          )}
          {activeTab === "takeAction" && (
            <Suspense fallback={<></>}>
              <TakeAction
                activeFilter={activeFilter}
                selectedProjectIds={selectedProjectIds}
                setSelectedProjectIds={setSelectedProjectIds}
              />
            </Suspense>
          )}
          {activeTab === "escrow" && (
            <Suspense fallback={<></>}>
              <Escrow />
            </Suspense>
          )}
          {activeTab === "support" && shouldShowSupport(portalData) && (
            <Suspense fallback={<></>}>
              <UserSupport />
            </Suspense>
          )}
          {activeTab === "exportedFiles" && (
            <Suspense fallback={<></>}>
              <ExportedFiles />
            </Suspense>
          )}
        </div>
      </div>
    </div>
  );
}

export default Notifications;
